/**
 * Copyright 2025, IntraLinks, Inc. All rights reserved.
 */

/* eslint-disable no-param-reassign */
import {
  createSlice,
  PayloadAction
} from '@reduxjs/toolkit';
import { AppThunk } from '../../../app/store';
import Repos from '../../../infrastructure/repositories/Repos';
import { IEmailPreferences } from '../../../domain/models/IEmailPreferences';
import { IProfileApps, TEmailFrequency } from '../../../domain/models/IProfileApps';
import { IPreference } from '../../../infrastructure/repositories/IEmailPreferencesRepository';
import { IDeal } from '../../../infrastructure/repositories/IDealCentreRepository';

export interface IEmailPreferencesState {
  fetchDealsFailed: boolean;
  fetchDefaultPreferencesFailed: boolean;
  fetchDealCenterPreferencesFailed: boolean;
  updateRequestFailed: boolean;
  updateRequestSuccess: boolean;
  fetchCompleted?: boolean;
  updateCompleted?: boolean;
  defaultPreferencesMap: Map<string, TEmailFrequency>;
  dealCenterPreferencesMap: Map<string, TEmailFrequency>;
  deals: IDeal[];
}

const initialState: IEmailPreferencesState = {
  fetchDealsFailed: false,
  fetchDefaultPreferencesFailed: false,
  fetchDealCenterPreferencesFailed: false,
  updateRequestFailed: false,
  updateRequestSuccess: false,
  fetchCompleted: false,
  updateCompleted: false,
  defaultPreferencesMap: new Map<string, TEmailFrequency>(),
  dealCenterPreferencesMap: new Map<string, TEmailFrequency>(),
  deals: []
};

export const emailPreferencesSlice = createSlice({
  name: 'emailPreferences',
  initialState,
  reducers: {
    setDefaultPreferencesMap: (state: IEmailPreferencesState, action: PayloadAction<Map<string, TEmailFrequency>>): void => {
      state.defaultPreferencesMap = action.payload;
    },
    setDealCentrePreferencesMap: (state: IEmailPreferencesState, action: PayloadAction<Map<string, TEmailFrequency>>): void => {
      state.dealCenterPreferencesMap = action.payload;
    },
    setDeals: (state: IEmailPreferencesState, action: PayloadAction<IDeal[]>): void => {
      state.deals = action.payload;
    },
    setFetchDealsFailed: (state: IEmailPreferencesState, action: PayloadAction<boolean>): void => {
      state.fetchDealsFailed = action.payload;
    },
    setFetchDefaultPreferencesFailed: (state: IEmailPreferencesState, action: PayloadAction<boolean>): void => {
      state.fetchDefaultPreferencesFailed = action.payload;
    },
    setFetchDealCenterPreferencesFailed: (state: IEmailPreferencesState, action: PayloadAction<boolean>): void => {
      state.fetchDealCenterPreferencesFailed = action.payload;
    },
    setUpdateRequestFailed: (state: IEmailPreferencesState, action: PayloadAction<boolean>): void => {
      state.updateRequestFailed = action.payload;
    },
    setUpdateRequestSuccess: (state: IEmailPreferencesState, action: PayloadAction<boolean>): void => {
      state.updateRequestSuccess = action.payload;
    },
    initiateFetch: (state: IEmailPreferencesState): void => {
      state.fetchCompleted = false;
      state.fetchDealsFailed = false;
      state.fetchDefaultPreferencesFailed = false;
      state.fetchDealCenterPreferencesFailed = false;
    },
    fetchCompleted: (state: IEmailPreferencesState): void => {
      state.fetchCompleted = true;
    },
    initiateUpdate: (state: IEmailPreferencesState): void => {
      state.updateCompleted = false;
      state.updateRequestFailed = false;
      state.updateRequestSuccess = false;
    },
    updateCompleted: (state: IEmailPreferencesState): void => {
      state.updateCompleted = true;
    }
  }
});

export const {
  setDefaultPreferencesMap,
  setDealCentrePreferencesMap,
  setDeals,
  setFetchDealsFailed,
  setFetchDefaultPreferencesFailed,
  setFetchDealCenterPreferencesFailed,
  setUpdateRequestFailed,
  setUpdateRequestSuccess,
  initiateFetch,
  fetchCompleted,
  initiateUpdate,
  updateCompleted
} = emailPreferencesSlice.actions;

export const updateEmailPreferences = (
  application: string,
  preferences: IEmailPreferences[]
): AppThunk => async (dispatch): Promise<void> => {
  dispatch(initiateUpdate());
  try {
    await Repos.instance.emailPreferencesRepository.updatePreferences(application, preferences);
    dispatch(setUpdateRequestSuccess(true));
  } catch (err) {
    dispatch(setUpdateRequestFailed(true));
  }
  dispatch(updateCompleted());
};

export const loadDefaultPreferences = (): AppThunk => async (dispatch): Promise<void> => {
  try {
    const defaultPreferences = await Repos.instance.emailPreferencesRepository.getDefaults();
    dispatch(setDefaultPreferencesMap(new Map<string, TEmailFrequency>(
      defaultPreferences.data.data
        .filter((dp: IProfileApps) => dp.preferenceSchema && dp.preferenceSchema.app)
        .map((dp: IProfileApps) => [
          dp.id,
          dp.preferenceSchema!.app.properties.frequency.default
        ])
    )));
    dispatch(setFetchDefaultPreferencesFailed(false));
  } catch (error) {
    dispatch(setFetchDefaultPreferencesFailed(true));
  }
};

const loadEmailPreferences = (
  application: string
): AppThunk => async (dispatch): Promise<void> => {
  try {
    const dealCentrePreferences = await Repos.instance.emailPreferencesRepository.getPreferences(application);
    dispatch(setDealCentrePreferencesMap(new Map<string, TEmailFrequency>(
      dealCentrePreferences.data.data.map((item: IPreference) => [
        item.containerId,
        item.preference.frequency
      ])
    )));
    dispatch(setFetchDealCenterPreferencesFailed(false));
  } catch (error) {
    dispatch(setFetchDealCenterPreferencesFailed(true));
  }
};

const loadDeals = (): AppThunk => async (dispatch): Promise<void> => {
  try {
    const deals = await Repos.instance.dealCentreRepository.getDeals();
    if (deals.data.data.length >= 0) {
      dispatch(setDeals(deals.data.data));
    }
    dispatch(setFetchDealsFailed(false));
  } catch (error) {
    dispatch(setFetchDealsFailed(true));
  }
};

export const loadPreferencesData = (application: string) : AppThunk => async (dispatch): Promise<void> => {
  dispatch(initiateFetch());
  await Promise.all([
    dispatch(loadDeals()),
    dispatch(loadEmailPreferences(application))
  ]);
  dispatch(fetchCompleted());
};

export default emailPreferencesSlice.reducer;
